import { useCallback, useEffect, useState } from "react";
import useEmblaCarousel from "embla-carousel-react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { HERO_SLIDES, buildWaLink } from "../data/menu";

export const HeroSlider = () => {
    const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true, align: "start" });
    const [selected, setSelected] = useState(0);

    const scrollTo = useCallback(
        (i) => emblaApi && emblaApi.scrollTo(i),
        [emblaApi],
    );

    useEffect(() => {
        if (!emblaApi) return;
        const onSelect = () => setSelected(emblaApi.selectedScrollSnap());
        emblaApi.on("select", onSelect);
        onSelect();
        const id = setInterval(() => {
            if (emblaApi) emblaApi.scrollNext();
        }, 5500);
        return () => clearInterval(id);
    }, [emblaApi]);

    return (
        <section
            id="home"
            className="relative w-full"
            data-testid="hero-slider-section"
        >
            <div className="embla" ref={emblaRef}>
                <div className="embla__container">
                    {HERO_SLIDES.map((s, idx) => (
                        <div
                            className="embla__slide relative h-[78vh] min-h-[560px] max-h-[820px]"
                            key={s.id}
                            data-testid={`hero-slide-${idx}`}
                        >
                            <img
                                src={s.image}
                                alt={s.title}
                                className="absolute inset-0 w-full h-full object-cover"
                                loading={idx === 0 ? "eager" : "lazy"}
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/55 to-black/20" />
                            <div className="relative z-10 h-full max-w-7xl mx-auto px-5 sm:px-8 flex flex-col justify-end pb-24 md:pb-32">
                                <div className="max-w-2xl">
                                    <span
                                        className="inline-block text-[11px] md:text-xs tracking-[0.32em] font-bold text-[#FFCBA0] bg-white/10 border border-white/25 backdrop-blur rounded-full px-3 py-1.5 fade-up"
                                        data-testid={`hero-eyebrow-${idx}`}
                                    >
                                        {s.eyebrow}
                                    </span>
                                    <h1
                                        className="font-display text-white text-4xl sm:text-5xl lg:text-6xl font-black leading-[1.05] tracking-tight mt-5 fade-up delay-1"
                                        data-testid={`hero-title-${idx}`}
                                    >
                                        {s.title}
                                    </h1>
                                    <p
                                        className="text-white/85 text-base sm:text-lg mt-4 max-w-xl leading-relaxed fade-up delay-2"
                                        data-testid={`hero-subtitle-${idx}`}
                                    >
                                        {s.subtitle}
                                    </p>
                                    <div className="mt-7 flex flex-wrap items-center gap-3 fade-up delay-3">
                                        <a
                                            href={buildWaLink(
                                                `Hi Kiraak Mandi! I'd like to order the ${s.title}.`,
                                            )}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="btn-press inline-flex items-center gap-2 bg-[#25D366] hover:bg-[#1EBE53] text-white font-bold px-6 py-3.5 rounded-full shadow-lg shadow-black/30"
                                            data-testid={`hero-cta-whatsapp-${idx}`}
                                        >
                                            <WaIcon className="w-5 h-5" />
                                            {s.cta}
                                        </a>
                                        <a
                                            href="#menu"
                                            className="btn-press inline-flex items-center gap-2 bg-white/10 backdrop-blur border border-white/35 hover:bg-white/20 text-white font-bold px-6 py-3.5 rounded-full"
                                            data-testid={`hero-cta-menu-${idx}`}
                                        >
                                            View Menu
                                        </a>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Prev / Next */}
            <button
                aria-label="Previous slide"
                onClick={() => emblaApi && emblaApi.scrollPrev()}
                className="hidden md:flex absolute left-5 top-1/2 -translate-y-1/2 z-20 items-center justify-center w-12 h-12 rounded-full bg-white/15 backdrop-blur border border-white/30 hover:bg-white/25 text-white btn-press"
                data-testid="hero-prev-btn"
            >
                <ChevronLeft className="w-6 h-6" />
            </button>
            <button
                aria-label="Next slide"
                onClick={() => emblaApi && emblaApi.scrollNext()}
                className="hidden md:flex absolute right-5 top-1/2 -translate-y-1/2 z-20 items-center justify-center w-12 h-12 rounded-full bg-white/15 backdrop-blur border border-white/30 hover:bg-white/25 text-white btn-press"
                data-testid="hero-next-btn"
            >
                <ChevronRight className="w-6 h-6" />
            </button>

            {/* Dots */}
            <div className="absolute bottom-6 left-0 right-0 z-20 flex items-center justify-center gap-2">
                {HERO_SLIDES.map((_, i) => (
                    <button
                        key={i}
                        onClick={() => scrollTo(i)}
                        aria-label={`Go to slide ${i + 1}`}
                        className={`btn-press h-2 rounded-full transition-all duration-300 ${
                            selected === i
                                ? "bg-[#D35400] w-9"
                                : "bg-white/60 hover:bg-white w-2.5"
                        }`}
                        data-testid={`hero-dot-${i}`}
                    />
                ))}
            </div>
        </section>
    );
};

const WaIcon = ({ className = "w-5 h-5" }) => (
    <svg viewBox="0 0 32 32" className={className} fill="currentColor" aria-hidden="true">
        <path d="M19.11 17.55c-.29-.14-1.7-.84-1.96-.94-.26-.1-.46-.14-.65.15-.19.29-.74.94-.91 1.13-.17.19-.33.22-.62.07-.29-.14-1.22-.45-2.32-1.43-.86-.76-1.44-1.71-1.61-2-.17-.29-.02-.45.13-.6.13-.13.29-.33.44-.5.15-.17.19-.29.29-.48.1-.19.05-.36-.02-.5-.07-.14-.65-1.57-.9-2.15-.24-.57-.48-.49-.65-.5-.17-.01-.36-.01-.55-.01-.19 0-.5.07-.76.36-.26.29-1 0.98-1 2.38 0 1.4 1.02 2.75 1.16 2.94.14.19 2 3.07 4.86 4.19.68.29 1.21.46 1.62.59.68.22 1.29.19 1.78.12.54-.08 1.7-.7 1.94-1.37.24-.67.24-1.24.17-1.37-.07-.13-.26-.22-.55-.36zM16 6C10.48 6 6 10.48 6 16c0 1.77.47 3.43 1.28 4.86L6 26l5.3-1.39A9.96 9.96 0 0 0 16 26c5.52 0 10-4.48 10-10S21.52 6 16 6z" />
    </svg>
);

export default HeroSlider;
